import React, { useState, useEffect, useCallback } from 'react';
import { Save, RotateCcw, Bot, Send } from 'lucide-react';
import {
  Button, NNSelect, StatusBadge, ICON_PX,
} from '../ui';

const DOMAIN_OPTIONS = [
  { value: 'quality', label: 'Quality' }, { value: 'staffing', label: 'Staffing' },
  { value: 'access', label: 'Access' }, { value: 'finance', label: 'Finance' },
  { value: 'compliance', label: 'Compliance' }, { value: 'safety', label: 'Safety' },
  { value: 'operations', label: 'Operations' }, { value: 'communications', label: 'Communications' },
];
const CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' }, { value: 'monthly', label: 'Monthly' },
];
const COMPLETION_TYPE_OPTIONS = [
  { value: 'checkbox', label: 'Checkbox' }, { value: 'checkbox_note', label: 'Checkbox + Note' },
  { value: 'checkbox_linked', label: 'Checkbox + Linked Output' },
];
const TIER_OPTIONS = [
  { value: 'A', label: 'Tier A — Same-day' }, { value: 'B', label: 'Tier B — 24h' },
  { value: 'C', label: 'Tier C — 72h' }, { value: 'D', label: 'Tier D — Flexible' },
];
const OWNER_ROLE_OPTIONS = [
  { value: 'com', label: 'COM' }, { value: 'triage_rn', label: 'Triage RN' },
  { value: 'charge_nurse', label: 'Charge Nurse' }, { value: 'front_desk', label: 'Front Desk' },
  { value: 'scheduling_coord', label: 'Scheduling Coord' },
];
const REGULATORY_OPTIONS = [
  { value: '', label: 'None' }, { value: 'joint_commission', label: 'Joint Commission' },
  { value: 'osha', label: 'OSHA' }, { value: 'cms', label: 'CMS' },
];

const inputClasses = 'w-full rounded-md border border-nn-border bg-nn-white px-3 py-1.5 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40';

/**
 * Inline right-panel task editor — replaces TaskDetailModal.
 * Shows task details when a task is selected, with inline editing and AI agent strip.
 */
function TaskEditorPanel({ task, onSave, onRestore, readOnly = false }) {
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [agentInput, setAgentInput] = useState('');

  useEffect(() => {
    if (task) {
      setForm({ ...task });
      setDirty(false);
    }
  }, [task]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    onSave?.(form);
    setDirty(false);
  }, [form, onSave]);

  const handleRestore = useCallback(() => {
    if (task?.id) {
      onRestore?.(task.id);
      setDirty(false);
    }
  }, [task, onRestore]);

  // AI Agent: simple keyword-based command parser (scaffold for LLM integration)
  const handleAgentSubmit = useCallback(() => {
    if (!agentInput.trim()) return;
    const cmd = agentInput.toLowerCase().trim();

    if (cmd.includes('daily')) handleChange('cadence', 'daily');
    else if (cmd.includes('weekly')) handleChange('cadence', 'weekly');
    else if (cmd.includes('biweekly')) handleChange('cadence', 'biweekly');
    else if (cmd.includes('monthly')) handleChange('cadence', 'monthly');

    if (cmd.includes('note requirement') || cmd.includes('add note')) handleChange('completionType', 'checkbox_note');
    if (cmd.includes('linked output') || cmd.includes('add linked')) handleChange('completionType', 'checkbox_linked');

    if (cmd.includes('tier a')) handleChange('criticalityTier', 'A');
    else if (cmd.includes('tier b')) handleChange('criticalityTier', 'B');
    else if (cmd.includes('tier c')) handleChange('criticalityTier', 'C');

    if (cmd.includes('charge nurse')) handleChange('defaultOwnerRole', 'charge_nurse');
    else if (cmd.includes('triage')) handleChange('defaultOwnerRole', 'triage_rn');
    else if (cmd.includes('front desk')) handleChange('defaultOwnerRole', 'front_desk');

    setAgentInput('');
  }, [agentInput, handleChange]);

  if (!task) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted text-body-sm">
        Select a task to view or edit details
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-nn-border shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-heading-sm text-text-dark truncate">{task.taskName || task.name}</h3>
          {task.isKP && <StatusBadge variant="info">KP</StatusBadge>}
          {!task.active && <StatusBadge variant="watch">Inactive</StatusBadge>}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1.5 shrink-0">
            {task.originalVersion && onRestore && (
              <Button variant="danger" size="sm" onClick={handleRestore}>
                <RotateCcw size={ICON_PX.md} className="mr-1" /> Restore
              </Button>
            )}
            <Button variant="save" size="sm" disabled={!dirty} onClick={handleSave}>
              <Save size={ICON_PX.md} className="mr-1" /> Save
            </Button>
          </div>
        )}
      </div>

      {/* Form Fields */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {/* Name */}
        <div>
          <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Task Name</label>
          {readOnly ? (
            <p className="text-body text-text-dark">{task.taskName || task.name}</p>
          ) : (
            <input
              type="text"
              value={form.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              className={inputClasses}
            />
          )}
        </div>

        {/* Domain + Cadence */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Domain</label>
            {readOnly ? (
              <p className="text-body text-text-dark capitalize">{task.domain}</p>
            ) : (
              <NNSelect value={form.domain || ''} onChange={(v) => handleChange('domain', v)} options={DOMAIN_OPTIONS} />
            )}
          </div>
          <div>
            <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Cadence</label>
            {readOnly ? (
              <p className="text-body text-text-dark capitalize">{task.cadence}</p>
            ) : (
              <NNSelect value={form.cadence || ''} onChange={(v) => handleChange('cadence', v)} options={CADENCE_OPTIONS} />
            )}
          </div>
        </div>

        {/* Completion Type + Est. Time */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Completion Type</label>
            {readOnly ? (
              <p className="text-body text-text-dark">{(task.completionType || '').replace(/_/g, ' + ')}</p>
            ) : (
              <NNSelect value={form.completionType || ''} onChange={(v) => handleChange('completionType', v)} options={COMPLETION_TYPE_OPTIONS} />
            )}
          </div>
          <div>
            <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Est. Time (min)</label>
            {readOnly ? (
              <p className="text-body text-text-dark">{task.estimatedMinutes} min</p>
            ) : (
              <input
                type="number"
                min={1}
                value={form.estimatedMinutes || ''}
                onChange={(e) => handleChange('estimatedMinutes', parseInt(e.target.value, 10) || 0)}
                className={inputClasses}
              />
            )}
          </div>
        </div>

        {/* Owner + Tier */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Owner Role</label>
            {readOnly ? (
              <p className="text-body text-text-dark capitalize">{(task.defaultOwnerRole || task.ownerRole || '').replace(/_/g, ' ')}</p>
            ) : (
              <NNSelect value={form.defaultOwnerRole || ''} onChange={(v) => handleChange('defaultOwnerRole', v)} options={OWNER_ROLE_OPTIONS} />
            )}
          </div>
          <div>
            <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Criticality</label>
            {readOnly ? (
              <p className="text-body text-text-dark">Tier {task.criticalityTier}</p>
            ) : (
              <NNSelect value={form.criticalityTier || 'C'} onChange={(v) => handleChange('criticalityTier', v)} options={TIER_OPTIONS} />
            )}
          </div>
        </div>

        {/* Regulatory Source */}
        <div>
          <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Regulatory Source</label>
          {readOnly ? (
            <p className="text-body text-text-dark">{task.regulatorySource ? task.regulatorySource.replace(/_/g, ' ') : 'None'}</p>
          ) : (
            <NNSelect value={form.regulatorySource || ''} onChange={(v) => handleChange('regulatorySource', v || null)} options={REGULATORY_OPTIONS} />
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Description</label>
          {readOnly ? (
            <p className="text-body-sm text-text-secondary">{task.description}</p>
          ) : (
            <textarea
              value={form.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className={`${inputClasses} resize-none`}
            />
          )}
        </div>

        {/* Tool URL */}
        <div>
          <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Tool URL</label>
          {readOnly ? (
            task.toolReference ? (
              <a href={task.toolReference} target="_blank" rel="noopener noreferrer" className="text-body text-aqua hover:text-aqua-bright transition-colors underline">{task.toolReference}</a>
            ) : (
              <p className="text-body text-text-muted">None</p>
            )
          ) : (
            <input
              type="url"
              value={form.toolReference || ''}
              onChange={(e) => handleChange('toolReference', e.target.value)}
              className={inputClasses}
              placeholder="https://..."
            />
          )}
        </div>
      </div>

      {/* AI Agent Strip */}
      {!readOnly && (
        <div className="shrink-0 border-t border-nn-border px-3 py-2 bg-nn-bg-subtle">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Bot size={ICON_PX.md} className="text-aqua" />
            <span className="text-caption font-bold text-text-muted uppercase tracking-wider">AI Assistant</span>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={agentInput}
              onChange={(e) => setAgentInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAgentSubmit(); }}
              placeholder="e.g., Make this biweekly, Add note requirement..."
              className="flex-1 rounded-md border border-nn-border bg-nn-white px-2.5 py-1.5 text-body-sm text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40"
            />
            <Button variant="default" size="sm" onClick={handleAgentSubmit} disabled={!agentInput.trim()}>
              <Send size={ICON_PX.sm} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskEditorPanel;
